import type { InputFieldSize } from "../types/inputfield";
import { themeVar } from "@config/theme/cssVar";
import {
	formControlHeight,
	formFieldBg,
	formFieldPlaceholderColor,
	formFieldSelectedColor,
	textFieldControlSx,
} from "@config/theme/formControls";

export type InputSurfaceVariant = "default" | "auth";

const presetSizes = new Set(["small", "medium", "large"]);

const resolveControlSize = (size: InputFieldSize) => {
	if (typeof size === "object" && size !== null) {
		return size;
	}
	return presetSizes.has(size) ? formControlHeight : formControlHeight;
};

const disabledReadableInputSx = {
	"& .MuiOutlinedInput-root.Mui-disabled": {
		backgroundColor: `${formFieldBg} !important`,
		opacity: 1,
	},
	"& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-input": {
		WebkitTextFillColor: "#6B7280",
		color: "#6B7280",
		opacity: 1,
	},
	"& .MuiOutlinedInput-root.Mui-disabled textarea.MuiOutlinedInput-input": {
		WebkitTextFillColor: "#6B7280",
		color: "#6B7280",
		opacity: 1,
	},
};

export const TextFieldStyles = (
	isClickable: boolean | undefined,
	size: InputFieldSize = "medium",
	error?: boolean,
	disabled?: boolean,
	surfaceVariant: InputSurfaceVariant = "default",
) => {
	const controlSize = resolveControlSize(size);
	const base = textFieldControlSx(error, controlSize) as Record<string, unknown>;

	const fieldStyles: Record<string, unknown> = {
		...base,
		cursor: isClickable ? "pointer" : "text",
		"& .MuiOutlinedInput-root": {
			backgroundColor: surfaceVariant === "auth" ? themeVar.surfaceMuted : themeVar.surface,
			overflow: "hidden",
		},
		...(surfaceVariant === "auth"
			? {
					"& .MuiOutlinedInput-root:not(.MuiInputBase-multiline) .MuiOutlinedInput-input:-webkit-autofill": {
						WebkitBoxShadow: `0 0 0 1000px ${themeVar.surfaceMuted} inset`,
					},
					"& .MuiOutlinedInput-root:not(.MuiInputBase-multiline) .MuiOutlinedInput-input:-webkit-autofill:hover": {
						WebkitBoxShadow: `0 0 0 1000px ${themeVar.surfaceMuted} inset`,
					},
					"& .MuiOutlinedInput-root:not(.MuiInputBase-multiline) .MuiOutlinedInput-input:-webkit-autofill:focus": {
						WebkitBoxShadow: `0 0 0 1000px ${themeVar.surfaceMuted} inset`,
					},
				}
			: {}),
		"& .MuiOutlinedInput-input": {
			cursor: isClickable ? "pointer" : "text",
			color: formFieldSelectedColor,
			"&.Mui-disabled": {
				WebkitTextFillColor: "#6B7280",
				color: "#6B7280",
				opacity: 1,
				cursor: "not-allowed",
			},
		},
		"& input::placeholder, & textarea::placeholder": {
			color: formFieldPlaceholderColor,
			fontFamily: "Poppins, sans-serif",
			fontSize: "14px",
			fontWeight: 400,
			opacity: 1,
		},
		"& input[type='date']": {
			"&:invalid": {
				color: "#6B7280",
			},
			"&::-webkit-datetime-edit": {
				color: "#6B7280",
			},
			"&:valid::-webkit-datetime-edit": {
				color: formFieldSelectedColor,
			},
			"&::-webkit-calendar-picker-indicator": {
				opacity: 0.6,
			},
		},
		"&.Mui-focused .MuiInputAdornment-root svg": {
			color: themeVar.primary,
		},
		"&.Mui-error .MuiInputAdornment-root svg": {
			color: themeVar.danger,
		},
		"& .MuiInputAdornment-positionStart": {
			marginRight: "0px",
		},
		"& .MuiInputAdornment-positionEnd": {
			marginLeft: "0px",
		},
		"& .MuiFormHelperText-root": {
			marginTop: "4px",
			fontSize: "11px",
			marginLeft: "0px",
			fontFamily: "Poppins, sans-serif",
			color: error ? "#FF6E4F" : "#6B7280",
			lineHeight: 1,
		},
		"& .MuiFormHelperText-root.Mui-error": {
			color: "#FF6E4F",
		},
	};

	return disabled ? { ...fieldStyles, ...disabledReadableInputSx } : fieldStyles;
};
